"use client";

import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { CheckIcon, ChevronDownIcon, CornerDownLeftIcon, PlusIcon } from "lucide-react";
import {
  type ComponentProps,
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Button } from "./button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "./command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover";
import { cn } from "../cn";

export type ComboboxData = {
  label: string;
  value: string;
};

type ComboboxContextType = {
  data: ComboboxData[];
  type: string;
  value: string;
  onValueChange: (value: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  width: number;
  setWidth: (width: number) => void;
  inputValue: string;
  setInputValue: (value: string) => void;
  portalContainer: HTMLElement | null;
  setPortalContainer: (container: HTMLElement | null) => void;
};

const ComboboxContext = createContext<ComboboxContextType>({
  data: [],
  type: "item",
  value: "",
  onValueChange: () => {},
  open: false,
  onOpenChange: () => {},
  width: 200,
  setWidth: () => {},
  inputValue: "",
  setInputValue: () => {},
  portalContainer: null,
  setPortalContainer: () => {},
});

export type ComboboxProps = ComponentProps<typeof Popover> & {
  data: ComboboxData[];
  type: string;
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export const Combobox = ({
  data,
  type,
  defaultValue,
  value: controlledValue,
  onValueChange: controlledOnValueChange,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  ...props
}: ComboboxProps) => {
  const [value, onValueChange] = useControllableState({
    defaultProp: defaultValue ?? "",
    prop: controlledValue,
    onChange: controlledOnValueChange,
  });
  const [open, onOpenChange] = useControllableState({
    defaultProp: defaultOpen,
    prop: controlledOpen,
    onChange: controlledOnOpenChange,
  });
  const [width, setWidth] = useState(200);
  const [inputValue, setInputValue] = useState("");
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null
  );

  return (
    <ComboboxContext.Provider
      value={{
        type,
        value,
        onValueChange,
        open,
        onOpenChange,
        data,
        width,
        setWidth,
        inputValue,
        setInputValue,
        portalContainer,
        setPortalContainer,
      }}
    >
      <Popover {...props} onOpenChange={onOpenChange} open={open} />
    </ComboboxContext.Provider>
  );
};

export type ComboboxTriggerProps = ComponentProps<typeof Button> & {
  asChild?: boolean;
  fullWidth?: boolean;
};

export const ComboboxTrigger = ({
  children,
  asChild,
  fullWidth,
  className,
  ...props
}: ComboboxTriggerProps) => {
  const { value, data, type, setWidth, setPortalContainer } =
    useContext(ComboboxContext);
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const trigger = ref.current;
    if (!trigger) return;

    const updateWidth = () => {
      if (trigger.offsetWidth) {
        setWidth(trigger.offsetWidth);
      }
    };

    updateWidth();
    setPortalContainer(
      trigger.closest<HTMLElement>("[data-slot='dialog-content']")
    );

    if (typeof ResizeObserver === "undefined") return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newWidth = (entry.target as HTMLElement).offsetWidth;
        if (newWidth) {
          setWidth(newWidth);
        }
      }
    });

    resizeObserver.observe(trigger);

    return () => {
      resizeObserver.disconnect();
    };
  }, [setPortalContainer, setWidth]);

  if (asChild) {
    return (
      <PopoverTrigger asChild ref={ref}>
        {children}
      </PopoverTrigger>
    );
  }

  return (
    <PopoverTrigger asChild>
      <Button
        variant="outline"
        className={cn(fullWidth && "w-full", className)}
        {...props}
        ref={ref}
      >
        {children ?? (
          <span className="flex w-full items-center justify-between gap-2">
            {value
              ? data.find((item) => item.value === value)?.label
              : `Select ${type}...`}
            <ChevronDownIcon
              className="shrink-0 text-muted-foreground"
              size={16}
            />
          </span>
        )}
      </Button>
    </PopoverTrigger>
  );
};

export type ComboboxContentProps = ComponentProps<typeof Command> & {
  popoverOptions?: ComponentProps<typeof PopoverContent>;
};

export const ComboboxContent = ({
  className,
  popoverOptions,
  ...props
}: ComboboxContentProps) => {
  const { width, portalContainer } = useContext(ComboboxContext);

  return (
    <PopoverContent
      className={cn("p-0", className)}
      style={{ width }}
      portalContainer={portalContainer}
      {...popoverOptions}
    >
      <Command {...props} />
    </PopoverContent>
  );
};

export type ComboboxInputProps = ComponentProps<typeof CommandInput> & {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
};

export const ComboboxInput = ({
  value: controlledValue,
  defaultValue,
  onValueChange: controlledOnValueChange,
  ...props
}: ComboboxInputProps) => {
  const { type, inputValue, setInputValue } = useContext(ComboboxContext);

  const [value, onValueChange] = useControllableState({
    defaultProp: defaultValue ?? inputValue,
    prop: controlledValue,
    onChange: (newValue) => {
      // Sync with context state
      setInputValue(newValue);
      // Call external onChange if provided
      controlledOnValueChange?.(newValue);
    },
  });

  return (
    <CommandInput
      onValueChange={onValueChange}
      placeholder={`Search ${type}...`}
      value={value}
      {...props}
    />
  );
};

export type ComboboxListProps = ComponentProps<typeof CommandList>;

export const ComboboxList = (props: ComboboxListProps) => (
  <CommandList {...props} />
);

export type ComboboxEmptyProps = ComponentProps<typeof CommandEmpty>;

export const ComboboxEmpty = ({ children, ...props }: ComboboxEmptyProps) => {
  const { type } = useContext(ComboboxContext);

  return (
    <CommandEmpty {...props}>{children ?? `No ${type} found.`}</CommandEmpty>
  );
};

export type ComboboxGroupProps = ComponentProps<typeof CommandGroup>;

export const ComboboxGroup = (props: ComboboxGroupProps) => (
  <CommandGroup {...props} />
);

export type ComboboxItemProps = ComponentProps<typeof CommandItem>;

export const ComboboxItem = (props: ComboboxItemProps) => {
  const { onValueChange, onOpenChange } = useContext(ComboboxContext);

  return (
    <CommandItem
      onSelect={(currentValue) => {
        onValueChange(currentValue);
        onOpenChange(false);
      }}
      {...props}
    />
  );
};

export type ComboboxSeparatorProps = ComponentProps<typeof CommandSeparator>;

export const ComboboxSeparator = (props: ComboboxSeparatorProps) => (
  <CommandSeparator {...props} />
);

export type SearchableSelectOption = ComboboxData & {
  disabled?: boolean;
  group?: string;
  icon?: ReactNode;
};

export type SearchableSelectProps = Omit<
  ComboboxProps,
  "data" | "type"
> & {
  options: SearchableSelectOption[];
  disabled?: boolean;
  type?: string;
  placeholder?: ReactNode;
  searchPlaceholder?: string;
  emptyMessage?: ReactNode;
  portalled?: boolean;
  renderOption?: (option: SearchableSelectOption) => ReactNode;
  renderValue?: (option: SearchableSelectOption) => ReactNode;
  footer?: ReactNode;
  /** Rendered at the right edge of the search input — e.g. `<ComboboxEnterHint />`. */
  inputTrailing?: ReactNode;
  triggerProps?: Omit<ComboboxTriggerProps, "children">;
  contentProps?: Omit<ComboboxContentProps, "children">;
};

export const SearchableSelect = ({
  options,
  type = "option",
  placeholder = "Select an option...",
  searchPlaceholder = "Search options...",
  emptyMessage = "No options found.",
  portalled = true,
  renderOption,
  renderValue,
  footer,
  inputTrailing,
  triggerProps,
  contentProps,
  disabled = false,
  value,
  defaultValue,
  onValueChange,
  ...props
}: SearchableSelectProps) => {
  const [selectedValue, setSelectedValue] = useControllableState({
    defaultProp: defaultValue ?? "",
    prop: value,
    onChange: onValueChange,
  });
  const selectedOption = options.find(
    (option) => option.value === selectedValue
  );
  const groups = new Map<string | undefined, SearchableSelectOption[]>();

  for (const option of options) {
    const group = groups.get(option.group) ?? [];
    group.push(option);
    groups.set(option.group, group);
  }

  const { popoverOptions, ...commandProps } = contentProps ?? {};

  return (
    <Combobox
      data={options}
      type={type}
      value={selectedValue}
      onValueChange={setSelectedValue}
      {...props}
    >
      <ComboboxTrigger
        {...triggerProps}
        disabled={disabled || triggerProps?.disabled}
      >
        <span className="flex min-w-0 w-full items-center justify-between gap-2">
          <span
            className={cn(
              "flex min-w-0 items-center gap-2 truncate",
              !selectedOption && "text-muted-foreground"
            )}
          >
            {selectedOption ? (
              renderValue?.(selectedOption) ?? (
                <>
                  {selectedOption.icon}
                  <span className="truncate">{selectedOption.label}</span>
                </>
              )
            ) : (
              <span className="truncate">{placeholder}</span>
            )}
          </span>
          <ChevronDownIcon
            className="shrink-0 text-muted-foreground"
            size={16}
          />
        </span>
      </ComboboxTrigger>
      <ComboboxContent
        {...commandProps}
        popoverOptions={{
          align: "start",
          portalled,
          ...popoverOptions,
        }}
      >
        <ComboboxInput placeholder={searchPlaceholder} trailing={inputTrailing} />
        <ComboboxList>
          <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
          {[...groups.entries()].map(([group, groupOptions], index) => (
            <ComboboxGroup key={group ?? `ungrouped-${index}`} heading={group}>
              {groupOptions.map((option) => (
                <ComboboxItem
                  key={option.value}
                  value={option.value}
                  keywords={[option.label, option.group ?? ""]}
                  disabled={option.disabled}
                >
                  <span className="flex min-w-0 flex-1 items-center gap-2">
                    {renderOption?.(option) ?? (
                      <>
                        {option.icon}
                        <span className="truncate">{option.label}</span>
                      </>
                    )}
                  </span>
                  <CheckIcon
                    className={cn(
                      "ml-auto size-4 shrink-0",
                      option.value === selectedValue
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                </ComboboxItem>
              ))}
            </ComboboxGroup>
          ))}
          {footer}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};

export type ComboboxCreateNewProps = {
  onCreateNew: (value: string) => void;
  children?: (inputValue: string) => ReactNode;
  className?: string;
};

export const ComboboxCreateNew = ({
  onCreateNew,
  children,
  className,
}: ComboboxCreateNewProps) => {
  const { inputValue, type, onValueChange, onOpenChange } =
    useContext(ComboboxContext);

  if (!inputValue.trim()) {
    return null;
  }

  const name = inputValue.trim();
  const handleCreateNew = () => {
    onCreateNew(name);
    onValueChange(name);
    onOpenChange(false);
  };

  // A real cmdk item, force-mounted so the filter never hides it: when the
  // search matches nothing else it is the only item, cmdk highlights it, and
  // Enter creates. With matches present it sits last and is reachable by
  // arrow keys. The sentinel value never collides with a data value.
  return (
    <CommandItem
      value="__combobox-create-new__"
      forceMount
      onSelect={handleCreateNew}
      className={cn("cursor-pointer", className)}
    >
      {children ? (
        children(name)
      ) : (
        <>
          <PlusIcon className="h-4 w-4 text-muted-foreground" />
          <span>{`Create new ${type}: "${name}"`}</span>
        </>
      )}
    </CommandItem>
  );
};

/**
 * "↵" hint at the right edge of the search input, shown once the user has
 * typed something — Enter picks the highlighted row, which is the create row
 * when nothing else matches. Pass via `SearchableSelect.inputTrailing` or
 * `ComboboxInput.trailing`.
 */
export const ComboboxEnterHint = ({ className }: { className?: string }) => {
  const { inputValue } = useContext(ComboboxContext);
  if (!inputValue.trim()) return null;
  return (
    <kbd
      aria-hidden
      title="Enter to select — or to create when nothing matches"
      className={cn(
        "inline-flex h-5 shrink-0 items-center rounded-sm border border-border px-1 text-muted-foreground",
        className
      )}
    >
      <CornerDownLeftIcon className="size-3" />
    </kbd>
  );
};
